import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Fab } from '@mui/material';
import DiamondTwoToneIcon from '@mui/icons-material/DiamondTwoTone';
import { keyframes } from '@emotion/react';

// Keyframes für die Flug-Animation
const flyToTarget = (startX, startY, endX, endY) => keyframes`
  0% {
    transform: translate(${startX}px, ${startY}px) scale(1) rotate(0deg);
    opacity: 1;
  }
  20% {
    transform: translate(${startX + (endX - startX) * 0.2}px, ${startY + (endY - startY) * 0.2}px) scale(1.2) rotate(72deg);
    opacity: 1;
  }
  50% {
    transform: translate(${startX + (endX - startX) * 0.6}px, ${startY + (endY - startY) * 0.3}px) scale(1.1) rotate(180deg);
    opacity: 0.9;
  }
  80% {
    transform: translate(${startX + (endX - startX) * 0.9}px, ${startY + (endY - startY) * 0.8}px) scale(0.9) rotate(288deg);
    opacity: 0.7;
  }
  100% {
    transform: translate(${endX}px, ${endY}px) scale(0.3) rotate(360deg);
    opacity: 0;
  }
`;

// Sparkle effect für den Zielpunkt
const sparkle = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  20% {
    transform: scale(1.5) rotate(72deg);
    opacity: 1;
  }
  40% {
    transform: scale(1.8) rotate(144deg);
    opacity: 0.8;
  }
  60% {
    transform: scale(1.2) rotate(216deg);
    opacity: 0.6;
  }
  80% {
    transform: scale(0.8) rotate(288deg);
    opacity: 0.4;
  }
  100% {
    transform: scale(0) rotate(360deg);
    opacity: 0;
  }
`;

const DiamondAnimation = ({ 
  trigger, 
  onAnimationComplete, 
  sourceElement, 
  targetElement,
  targetResolver, // New prop for dynamic target resolution
  amount = "1.000",
  diamondCount = 5 
}) => {
  const [diamonds, setDiamonds] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    
    if (!sourceElement) {
      console.error('💎 No source element provided');
      return;
    }

    // Resolve target element dynamically if targetResolver is provided
    const resolvedTargetElement = targetResolver ? targetResolver() : targetElement;
    
    if (!resolvedTargetElement) {
      console.error('💎 No target element provided or resolved');
      return;
    }

    // Hole die Positionen der Elemente
    const sourceRect = sourceElement.getBoundingClientRect();
    const targetRect = resolvedTargetElement.getBoundingClientRect();
    

    // Überprüfe, ob die Elemente gültige Positionen haben
    if (sourceRect.width === 0 || sourceRect.height === 0) {
      console.error('💎 Source element has invalid dimensions');
      return;
    }
    
    if (targetRect.width === 0 || targetRect.height === 0) {
      console.error('💎 Target element has invalid dimensions');
      return;
    }

    // Erstelle mehrere Diamanten mit leichten Abweichungen
    const newDiamonds = Array.from({ length: diamondCount }, (_, index) => {
      const delay = index * 100; // 100ms Verzögerung zwischen den Diamanten
      const randomOffset = {
        x: (Math.random() - 0.5) * 100, // Zufällige X-Abweichung
        y: (Math.random() - 0.5) * 50   // Zufällige Y-Abweichung
      };

      return {
        id: Date.now() + index,
        startX: sourceRect.left + sourceRect.width / 2 + randomOffset.x,
        startY: sourceRect.top + sourceRect.height / 2 + randomOffset.y,
        endX: targetRect.left + targetRect.width / 2,
        endY: targetRect.top + targetRect.height / 2,
        delay,
        size: 20 + Math.random() * 15 // Verschiedene Größen
      };
    });

    setDiamonds(newDiamonds);

    // Entferne Diamanten nach der Animation
    const cleanup = setTimeout(() => {
      
      setDiamonds([]);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }, 2000 + (diamondCount * 100));

    return () => clearTimeout(cleanup);
  }, [trigger, sourceElement, targetElement, targetResolver, diamondCount, onAnimationComplete]);

  // Rendered via a portal straight to document.body so the animation always sits above the
  // rest of the page, regardless of any ancestor (e.g. the topbar) creating its own CSS
  // stacking context - a plain high z-index alone only wins within the nearest stacking
  // context, not necessarily the whole page.
  return createPortal(
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden'
      }}
    >
      {diamonds.map((diamond) => (
        <DiamondTwoToneIcon
          key={diamond.id}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: `${diamond.size}px`,
            color: '#6c3eb6',
            animation: `${flyToTarget(
              diamond.startX - diamond.size / 2,
              diamond.startY - diamond.size / 2,
              diamond.endX - diamond.size / 2,
              diamond.endY - diamond.size / 2
            )} 2s ease-in-out ${diamond.delay}ms forwards`,
            filter: 'drop-shadow(0 0 8px rgba(108, 62, 182, 0.6))',
            transformOrigin: 'center'
          }}
        />
      ))}
      
      {/* Sparkle-Effekt am Ziel */}
      {diamonds.length > 0 && (
        <DiamondTwoToneIcon
          sx={{
            position: 'absolute',
            top: diamonds[0]?.endY - 15,
            left: diamonds[0]?.endX - 15,
            fontSize: '30px',
            color: '#6c3eb6',
            animation: `${sparkle} 1s ease-out 1.5s forwards`,
            opacity: 0,
            filter: 'drop-shadow(0 0 12px rgba(108, 62, 182, 0.8))'
          }}
        />
      )}
    </Box>,
    document.body
  );
};

export default DiamondAnimation;
